import Http from '../services/http.service';
import { HttpStatusCode } from 'axios';

// UTM keys we propagate from sessionStorage → query string on the lead submit.
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign'];

/**
 * Build the URL query string for UTM propagation.
 * UTMs are stored in sessionStorage on app load (see captureUtmParams).
 */
const buildUtmQuery = () => {
  if (typeof sessionStorage === 'undefined') return '';
  const params = new URLSearchParams();
  UTM_KEYS.forEach((k) => {
    const v = sessionStorage.getItem(k);
    if (v) params.set(k, v);
  });
  const qs = params.toString();
  return qs ? `?${qs}` : '';
};

/**
 * Reads UTM params from the current URL (window.location) and saves them
 * to sessionStorage so they survive navigation between steps. Call once
 * at app startup (see main.js / App.vue).
 */
export const captureUtmParams = () => {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  UTM_KEYS.forEach((k) => {
    const v = url.searchParams.get(k);
    if (v) sessionStorage.setItem(k, v);
  });
};

export const useDiagnostic = () => {
  /**
   * POST /api/diagnostic/leads — saves the diagnostic lead.
   *
   * @param {object} payload — { name, company, email, lang, answers, score, percentile, recommendedRoute }
   * @returns {Promise<object>} normalized result:
   *   { ok: true,  data: { leadId, createdAt } }
   *   { ok: false, code: 'DUPLICATE_LEAD'   }
   *   { ok: false, code: 'RATE_LIMITED'     }
   *   { ok: false, code: 'VALIDATION_ERROR', field: 'email' }
   *   { ok: false, code: 'SERVER_ERROR',    message: '...' }
   */
  const saveDiagnosticLead = async (payload) => {
    try {
      const url = `/api/diagnostic/leads${buildUtmQuery()}`;
      const { status, data } = await Http.post(url, payload, { skipCompanyId: true });

      if (status === HttpStatusCode.Created || status === HttpStatusCode.Ok) {
        return { ok: true, data: data?.data ?? {} };
      }
      return { ok: false, code: data?.code || 'SERVER_ERROR', message: data?.message };
    } catch (error) {
      const res = error?.response;
      const code = res?.data?.code;
      const message = res?.data?.message;
      if (res?.status === HttpStatusCode.Conflict || code === 'DUPLICATE_LEAD') {
        return { ok: false, code: 'DUPLICATE_LEAD', message };
      }
      if (res?.status === HttpStatusCode.TooManyRequests || code === 'RATE_LIMITED') {
        return { ok: false, code: 'RATE_LIMITED', message };
      }
      if (res?.status === HttpStatusCode.BadRequest || code === 'VALIDATION_ERROR') {
        const field = res?.data?.errors?.[0]?.field;
        return { ok: false, code: 'VALIDATION_ERROR', field, message };
      }
      return { ok: false, code: 'SERVER_ERROR', message: message || error.message };
    }
  };

  return {
    saveDiagnosticLead,
  };
};
